import type { MarketingRoleSlug } from './role-page-model';
import type { MarketingRolePageSource } from './role-page-content.types';

type FirstRoleSlug = Extract<
  MarketingRoleSlug,
  | 'founders-emerging-brands'
  | 'creative-directors'
  | 'fashion-designers'
  | 'technical-design-development'
>;

export const MARKETING_ROLE_PAGE_CONTENT_A = {
  'founders-emerging-brands': {
    heroBody: 'Launch the right products without wasting limited capital.',
    composerPlaceholder: 'What are you building?',
    useCases: [
      {
        label: 'Clarify Customer and Positioning',
        prompt:
          "Help me define my brand's target customer and positioning. Compare relevant competitors, identify an unmet need, and draft a value proposition with evidence to validate. Ask about my brand, target customer, product category, launch market, budget, and timeline before making recommendations. Use the references and data I provide, flag missing information and assumptions, and finish with prioritized recommendations and concrete next steps.",
      },
      {
        label: 'Test Product Ideas Early',
        prompt:
          'Help me test product ideas before investing in production. Compare customer appeal, differentiation, feasibility, and risk, then propose low-cost experiments and clear decision criteria. Ask about my brand, target customer, product category, launch market, budget, and timeline before making recommendations. Use the references and data I provide, flag missing information and assumptions, and finish with prioritized recommendations and concrete next steps.',
      },
      {
        label: 'Build a Focused Collection',
        prompt:
          'Build a focused first collection for my brand. Recommend hero products, supporting styles, colorways, sizes, and a SKU count that fits my budget. Ask about my brand, target customer, product category, launch market, budget, and timeline before making recommendations. Use the references and data I provide, flag missing information and assumptions, and finish with prioritized recommendations and concrete next steps.',
      },
      {
        label: 'Know Cost, MOQ and Margin',
        prompt:
          'Estimate unit costs, minimum order quantities, and margins for my product. Compare order-volume scenarios, show the cost breakdown, and distinguish estimates from supplier quotes. Ask about my brand, target customer, product category, launch market, budget, and timeline before making recommendations. Use the references and data I provide, flag missing information and assumptions, and finish with prioritized recommendations and concrete next steps.',
      },
      {
        label: 'Get Factory and Buyer Ready',
        prompt:
          'Prepare my product for factory and buyer discussions. Create a checklist covering technical specifications, materials, costing, samples, line sheets, and unanswered commercial questions. Ask about my brand, target customer, product category, launch market, budget, and timeline before making recommendations. Use the references and data I provide, flag missing information and assumptions, and finish with prioritized recommendations and concrete next steps.',
      },
      {
        label: 'Launch, Learn and Improve',
        prompt:
          'Build a launch-and-learning plan for my brand. Define launch activities, customer feedback methods, performance metrics, and a review schedule for deciding what to improve. Ask about my brand, target customer, product category, launch market, budget, and timeline before making recommendations. Use the references and data I provide, flag missing information and assumptions, and finish with prioritized recommendations and concrete next steps.',
      },
    ],
    whyCards: [
      {
        title: 'Clarify Customer and Positioning',
        body: 'Define who you serve, what makes the brand different, and where it belongs in the market.',
        imageAlt: 'Customer, market, and brand-positioning directions',
      },
      {
        title: 'Test Product Ideas Early',
        body: 'Create realistic concepts and collect customer feedback before paying for development.',
        imageAlt: 'Early product concepts ready for customer testing',
      },
      {
        title: 'Build a Focused Collection',
        body: 'Choose the right products, colors, quantities, and price points for the launch.',
        imageAlt: 'A focused launch assortment organized by product and color',
      },
    ],
    insights: [
      {
        title: 'Know Cost, MOQ and Margin',
        body: 'Understand product economics and total cash required before committing to production.',
      },
      {
        title: 'Get Factory and Buyer Ready',
        body: 'Prepare technical files, costing, line sheets, lookbooks, and product presentations.',
      },
      {
        title: 'Launch, Learn and Improve',
        body: 'Launch through Shopify, track what sells, and use the results to plan the next drop.',
      },
    ],
    cta: {
      eyebrow: 'Founders & Emerging Brands',
      heading: 'Get started with IMAI now.',
      cards: [
        { label: 'Build a Focused Collection', href: '/auth?mode=signup' },
        { label: 'Discuss Your Brand Launch', href: '/contact' },
      ],
    },
  },
  'creative-directors': {
    heroBody: 'Your creative vision carried through every department.',
    whyDescription:
      'Protect your creative vision from initial sketch to final production sample.',
    composerPlaceholder: 'Build anything with IMAI',
    useCases: [
      {
        label: 'Reinterpret Brand Codes for Today',
        prompt:
          "Reinterpret my brand's signature codes for the coming season. Identify what must remain recognizable and propose fresh silhouettes, palettes, materials, and styling with rationale. Ask about my brand identity, customer, season, collection goals, and creative references before developing the direction. Use the references and data I provide, flag missing information and assumptions, and finish with prioritized recommendations and concrete next steps.",
      },
      {
        label: 'Compare Multiple Seasonal Directions',
        prompt:
          'Develop and compare distinct seasonal creative directions. Show the concept, references, silhouettes, colors, materials, customer relevance, and tradeoffs, then recommend a direction to explore. Ask about my brand identity, customer, season, collection goals, and creative references before developing the direction. Use the references and data I provide, flag missing information and assumptions, and finish with prioritized recommendations and concrete next steps.',
      },
      {
        label: 'Edit the Collection as One',
        prompt:
          'Review my collection as a whole. Identify inconsistent styles, missing links, and repeated ideas, then recommend edits that strengthen the story and commercial balance. Ask about my brand identity, customer, season, collection goals, and creative references before developing the direction. Use the references and data I provide, flag missing information and assumptions, and finish with prioritized recommendations and concrete next steps.',
      },
      {
        label: "Select the Season's Hero Products",
        prompt:
          "Help me select the season's hero products. Compare brand relevance, visual impact, customer demand, margin potential, and feasibility, then rank candidates with supporting reasons. Ask about my brand identity, customer, season, collection goals, and creative references before developing the direction. Use the references and data I provide, flag missing information and assumptions, and finish with prioritized recommendations and concrete next steps.",
      },
      {
        label: 'Align Product and Campaign Storytelling',
        prompt:
          "Connect my collection's product story to its campaign. Develop a central narrative, key messages, styling, visual direction, and asset briefs grounded in the actual products. Ask about my brand identity, customer, season, collection goals, and creative references before developing the direction. Use the references and data I provide, flag missing information and assumptions, and finish with prioritized recommendations and concrete next steps.",
      },
      {
        label: 'Unify Apparel, Footwear and Accessories',
        prompt:
          "Create one coherent direction across apparel, footwear, and accessories. Align color, material, proportion, and signature details while preserving each category's practical requirements. Ask about my brand identity, customer, season, collection goals, and creative references before developing the direction. Use the references and data I provide, flag missing information and assumptions, and finish with prioritized recommendations and concrete next steps.",
      },
    ],
    whyCards: [
      {
        title: 'Realize Your Vision with Taste',
        body: 'Bring the brief, references, brand codes, audience, and product ambition into one visible starting point.',
        imageAlt: 'A creative direction expressed across a complete collection',
        details: [
          'Your codes, learned. Every output arrives on-brand. Every correction refines it.',
          'Intent survives the handoff. What comes back is what you meant.',
          'Beauty that can be built. "Approve more, annotate less"',
        ],
      },
      {
        title: 'Build Without the Back-and-Forth',
        body: 'Hand the factory documentation complete enough to build from. The first sample reflects your true intent.',
        imageAlt: 'Approved design decisions connected to factory documents',
        details: [
          'Explain once. Every answer lives in the documentation. No repeated emails.',
          'First samples, closer to final. Complete specs, fewer surprise rounds.',
          'Nothing lost in translation. Intent arrives in language a factory can build from.',
        ],
      },
      {
        title: 'Always on Budget',
        body: 'Find the strongest materials your budget can buy, with price, season, and brand fit visible from the first sketch.',
        imageAlt: 'Materials and costs compared beside the design',
        details: [
          'Design against your budget from day one. Not compromise after the first quote.',
          "New tariffs, same cost. When tariffs redraw the sourcing map, see each material's origin and cost impact before you order.",
          'Swap materials and compare side by side. Budget and beauty move together.',
        ],
      },
    ],
    insights: [
      {
        title: 'Sketch to render',
        body: 'Turn a rough sketch or reference into a realistic product image in the brand’s visual language.',
      },
      {
        title: 'Colorway and fabric control',
        body: 'Recolor a style to the approved palette and compare fabric, texture, drape, and price together.',
      },
      {
        title: 'Product to campaign',
        body: 'Carry the approved design into technical flats, on-model imagery, and campaign scenes.',
      },
    ],
    cta: {
      eyebrow: 'One creative direction, every department',
      heading: 'Get started with IMAI now.',
      cards: [
        { label: 'Direct your first project', href: '/auth?mode=signup' },
        {
          label: 'Discuss Your Creative Direction',
          href: '/contact',
        },
      ],
    },
  },
  'fashion-designers': {
    heroBody: 'Your design intent, carried from first sketch to final sample.',
    composerPlaceholder: 'Design anything with IMAI',
    useCases: [
      {
        label: 'Visualize Rough Sketches Instantly',
        prompt:
          'Turn my rough sketches into clear design concepts. Preserve the intended proportions and details, identify ambiguous areas, and propose visual directions for my review. Ask for my sketches, reference images, customer, season, materials, and price target before developing the design. Use the references and data I provide, flag missing information and assumptions, and finish with prioritized recommendations and concrete next steps.',
      },
      {
        label: 'Explore Silhouettes Before Sampling',
        prompt:
          'Explore silhouette alternatives before physical sampling. Compare proportions, volume, fit intent, movement, and construction implications, and recommend the most promising options to develop. Ask for my sketches, reference images, customer, season, materials, and price target before developing the design. Use the references and data I provide, flag missing information and assumptions, and finish with prioritized recommendations and concrete next steps.',
      },
      {
        label: 'Test Fabrics on Any Design',
        prompt:
          'Compare fabric options on my design. Explain how drape, texture, weight, stretch, care, and cost could affect the result, and flag properties requiring physical tests. Ask for my sketches, reference images, customer, season, materials, and price target before developing the design. Use the references and data I provide, flag missing information and assumptions, and finish with prioritized recommendations and concrete next steps.',
      },
      {
        label: 'Build Colorways Without Redrawing',
        prompt:
          'Build colorway options from my existing design without changing its construction. Propose coordinated palettes, assign colors to components, and recommend a balanced final selection. Ask for my sketches, reference images, customer, season, materials, and price target before developing the design. Use the references and data I provide, flag missing information and assumptions, and finish with prioritized recommendations and concrete next steps.',
      },
      {
        label: 'Build Collections From One Idea',
        prompt:
          'Expand one design idea into a cohesive collection. Define the shared design language, complementary styles, materials, palettes, and variation rules without repeating the same product. Ask for my sketches, reference images, customer, season, materials, and price target before developing the design. Use the references and data I provide, flag missing information and assumptions, and finish with prioritized recommendations and concrete next steps.',
      },
      {
        label: 'Prepare Clear Factory Handoffs',
        prompt:
          'Prepare a clear factory handoff for my design. Organize flats, construction notes, materials, colorways, measurements, and open questions into a specification checklist for review. Ask for my sketches, reference images, customer, season, materials, and price target before developing the design. Use the references and data I provide, flag missing information and assumptions, and finish with prioritized recommendations and concrete next steps.',
      },
    ],
    whyCards: [
      {
        title: 'Stay in the Flow',
        body: 'Move from inspiration to a visible product while the idea is still fresh, without rebuilding the style across disconnected files.',
        imageAlt: 'A rough fashion sketch becoming a realistic style',
      },
      {
        title: 'Make Decisions You Can See',
        body: 'Compare silhouettes, colors, materials, and details in context before deciding what deserves a sample.',
        imageAlt: 'Fashion variations compared side by side',
      },
      {
        title: 'Protect Every Design Decision',
        body: 'Carry the approved look into development with its color, material, trim, finish, and construction intent intact.',
        imageAlt: 'Approved design details connected to technical development',
      },
    ],
    insights: [
      {
        title: 'Sketch to Style',
        body: 'Turn a hand sketch or technical flat into a realistic visual while preserving its silhouette and key details.',
      },
      {
        title: 'Fabric and Colorway Exploration',
        body: 'See one design across different materials, textures, weights, finishes, and palettes before sampling.',
      },
      {
        title: 'Photo to Technical Flat',
        body: 'Turn a sample photo or product render into clean front and back flat sketches ready for development.',
      },
    ],
    faq: {
      label: 'Frequently Asked Questions',
      heading: 'FAQ',
      items: [
        {
          question: 'Will the final design still feel like mine?',
          answer:
            'Yes. You establish the silhouette, references, colors, materials, and details, then decide what advances, changes, or gets rejected.',
        },
        {
          question: 'Can I start with a rough hand sketch?',
          answer:
            'Yes. Begin with a sketch, flat, reference image, product photo, fabric, palette, or written direction.',
        },
        {
          question: 'How precisely can I control the design?',
          answer:
            'Direct changes to silhouette, proportion, length, color, material, pattern, trims, finishes, and construction details while keeping the rest consistent.',
        },
        {
          question: 'Can an approved design move into technical development?',
          answer:
            'Yes. Product details and decisions stay connected so technical and development teams begin with the version you approved.',
        },
      ],
    },
    cta: {
      eyebrow: 'From first sketch to final sample',
      heading: 'Turn your next sketch into something real.',
      cards: [
        { label: 'Create your first design', href: '/auth?mode=signup' },
        {
          label: 'Discuss Your Design Workflow',
          href: '/contact',
        },
      ],
    },
  },
  'technical-design-development': {
    heroBody:
      'Get the first sample closer to final with clear specifications, construction details, BOMs, fit comments, and controlled revisions.',
    composerPlaceholder: 'Prepare any product for production',
    useCases: [
      {
        label: 'Draft Complete Tech Packs',
        prompt:
          'Draft a complete tech pack from my approved design. Include flats, measurements, tolerances, grading requirements, materials, construction, labels, packaging, and quality checks requiring confirmation. Ask for the latest design, specifications, measurements, sample feedback, materials, and production constraints before proposing changes. Use the references and data I provide, flag missing information and assumptions, and finish with prioritized recommendations and concrete next steps.',
      },
      {
        label: 'Review Samples Against Specs',
        prompt:
          'Review my sample against the approved specifications. Compare measurements, construction, materials, color, and finishing, then prioritize deviations with evidence and proposed corrective actions. Ask for the latest design, specifications, measurements, sample feedback, materials, and production constraints before proposing changes. Use the references and data I provide, flag missing information and assumptions, and finish with prioritized recommendations and concrete next steps.',
      },
      {
        label: 'Turn Fit Notes Into Revisions',
        prompt:
          'Turn fit notes into actionable revisions. Organize issues by body area, connect each to measurements or construction, and draft clear revision instructions for the next sample. Ask for the latest design, specifications, measurements, sample feedback, materials, and production constraints before proposing changes. Use the references and data I provide, flag missing information and assumptions, and finish with prioritized recommendations and concrete next steps.',
      },
      {
        label: 'Keep Every Version Aligned',
        prompt:
          'Reconcile product versions across design and development files. Identify conflicting specifications, summarize changes, and propose an approval record that makes the latest agreed version clear. Ask for the latest design, specifications, measurements, sample feedback, materials, and production constraints before proposing changes. Use the references and data I provide, flag missing information and assumptions, and finish with prioritized recommendations and concrete next steps.',
      },
      {
        label: 'Catch Construction Issues Earlier',
        prompt:
          'Review the design for construction problems before sampling. Check seams, tolerances, component interfaces, and assembly sequence, and flag issues needing a technician or factory review. Ask for the latest design, specifications, measurements, sample feedback, materials, and production constraints before proposing changes. Use the references and data I provide, flag missing information and assumptions, and finish with prioritized recommendations and concrete next steps.',
      },
      {
        label: 'Compare Materials Against Target Cost',
        prompt:
          'Compare material alternatives against my target cost. Assess performance, appearance, availability, wastage, and processing implications, then recommend options with explicit cost and quality tradeoffs. Ask for the latest design, specifications, measurements, sample feedback, materials, and production constraints before proposing changes. Use the references and data I provide, flag missing information and assumptions, and finish with prioritized recommendations and concrete next steps.',
      },
    ],
    whyCards: [
      {
        title: 'First Samples, Closer to Final',
        body: 'Give the factory a complete, reviewable technical starting point before the first sample is cut.',
        imageAlt: 'A complete technical pack prepared for the first sample',
      },
      {
        title: 'One Version Everyone Trusts',
        body: 'Keep measurements, fit comments, construction corrections, and approvals connected to the current style.',
        imageAlt:
          'Sample revisions and approvals connected to one product version',
      },
      {
        title: 'Approve Production With Confidence',
        body: 'Know what is resolved, what remains open, and what changed before the style reaches bulk production.',
        imageAlt:
          'A production-readiness review with resolved and open decisions',
      },
    ],
    insights: [
      {
        title: 'Tech Pack First Draft',
        body: 'Build a structured draft with technical flats, specifications, construction callouts, materials, colors, and BOM details.',
      },
      {
        title: 'Fit Review and Revision Control',
        body: 'Compare samples to approved specifications and turn every fit observation into a clear factory action.',
      },
      {
        title: 'Factory Handoff Package',
        body: 'Package the latest tech pack, BOM, flats, specifications, fit comments, and approvals into one vendor-ready handoff.',
      },
    ],
    faq: {
      label: 'Frequently Asked Questions',
      heading: 'FAQ',
      items: [
        {
          question: 'How accurate is the first tech-pack draft?',
          answer:
            'It provides a structured starting point. A technical designer still reviews and approves measurements, tolerances, grading, construction, materials, and production requirements.',
        },
        {
          question: 'Can we use our existing templates and standards?',
          answer:
            'Yes. Tech packs can follow your format, terminology, points of measure, tolerances, grading rules, construction standards, and vendor requirements.',
        },
        {
          question: 'How are fit comments and sample rounds tracked?',
          answer:
            'Each round remains connected to its measurements, images, annotations, comments, requested corrections, and approval status.',
        },
        {
          question: 'How much control does the technical designer retain?',
          answer:
            'Complete control. Fit, construction, grading, quality, and production approval remain professional decisions.',
        },
      ],
    },
    cta: {
      eyebrow: 'A clear, controlled product record',
      heading: 'Get the first sample closer to final.',
      cards: [
        { label: 'Build your first tech pack', href: '/auth?mode=signup' },
        {
          label: 'Discuss Your Product Development',
          href: '/contact',
        },
      ],
    },
  },
} as const satisfies Record<FirstRoleSlug, MarketingRolePageSource>;
